import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { CatalogFrame } from "./useFrameCatalog";

interface RefFrame {
  id: string;
  color: string | null;
  color_en: string | null;
  shape: string | null;
  shape_en: string | null;
  collection_name: string | null;
}

/**
 * Charge un petit sous-ensemble de montures similaires (même forme OU même couleur
 * OU même collection) depuis `country_catalog_mv`. Remplace un chargement complet
 * du catalogue pays quand on n'a besoin que de ~6 vignettes de suggestions.
 */
export function useSimilarFrames(frame: RefFrame | null | undefined, countryId: string | undefined) {
  const norm = (v: string | null | undefined) => (v ?? "").trim();
  const color = norm(frame?.color) || norm(frame?.color_en);
  const colorEn = norm(frame?.color_en);
  const shape = norm(frame?.shape) || norm(frame?.shape_en);
  const shapeEn = norm(frame?.shape_en);
  const collection = norm(frame?.collection_name);

  return useQuery({
    queryKey: ["similar-frames", "mv-v1", countryId, frame?.id, shape, color, collection],
    enabled: !!countryId && !!frame?.id,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<CatalogFrame[]> => {
      // Build OR filter — n'inclut que les critères présents.
      const orParts: string[] = [];
      const push = (col: string, val: string) => {
        if (!val) return;
        const escaped = val.replace(/"/g, '\\"');
        orParts.push(`${col}.eq."${escaped}"`);
      };
      push("shape", shape);
      push("shape_en", shapeEn || shape);
      push("color", color);
      push("color_en", colorEn || color);
      push("collection_name", collection);

      if (orParts.length === 0) return [];

      const [{ data }, minLensRes] = await Promise.all([
        (supabase.from as any)("country_catalog")
          .select(
            "frame_id, sku, slug, name, name_en, gender, shape, shape_en, material, material_en, color, color_en, collection_name, base_image_url, gallery, description, description_en, size_label_en, frame_price_ttc, stock_quantity, is_lapaire_plus, is_sunglasses"
          )
          .eq("country_id", countryId!)
          .eq("is_available", true)
          .gt("stock_quantity", 0)
          .eq("has_image", true)
          .neq("frame_id", frame!.id)
          .or(orParts.join(","))
          .limit(60),
        (supabase.from as any)("active_lens_catalog")
          .select("price_ttc")
          .eq("country_id", countryId!)
          .order("price_ttc", { ascending: true })
          .limit(1),
      ]);

      const rows = (data ?? []) as any[];
      const minLensPrice = Number(minLensRes.data?.[0]?.price_ttc ?? 0);
      return rows.map((r) => ({
        id: r.frame_id,
        sku: r.sku,
        slug: r.slug,
        name: r.name,
        gender: r.gender,
        shape: r.shape,
        material: r.material,
        color: r.color,
        collection_name: r.collection_name,
        base_image_url: r.base_image_url,
        gallery: Array.isArray(r.gallery) ? (r.gallery as string[]) : [],
        description: r.description,
        framePriceTtc: Number(r.frame_price_ttc ?? 0),
        startingPrice: Number(r.frame_price_ttc ?? 0) + minLensPrice,
        inStock: true,
        stockQuantity: Number(r.stock_quantity ?? 0),
        isLapairePlus: r.is_lapaire_plus === true,
        isSunglasses: r.is_sunglasses === true,
        name_en: r.name_en ?? null,
        color_en: r.color_en ?? null,
        material_en: r.material_en ?? null,
        shape_en: r.shape_en ?? null,
        size_label_en: r.size_label_en ?? null,
        description_en: r.description_en ?? null,
      }));
    },
  });
}
